import React, {createContext, ReactNode, useContext, useEffect, useState} from "react";
import userCapabilityService from "../services/userCapabilityService";
import {useViewerStore} from "../store/store";
import { useOneButtonStore } from "../store/oneButtonStore";
import {useIsAuthenticated} from "@azure/msal-react";
import { resolveGhostOnlyGreenImpactTreesEnabled } from "../config/featureFlags";

interface UserContextType {
    user: User | null,
    hasCapability: (name: string) => boolean,
}

const UserContext = createContext<UserContextType | null>(null);

const useCurrentUser = () => {
    const currentUserContext = useContext(UserContext);

    if (!currentUserContext) {
        throw new Error("useCurrentUser has to be used within <CurrentUserContext.Provider>");
    }

    return currentUserContext;
};

interface UserProviderProps {
    children: ReactNode;
}

const UserProvider: React.FC<UserProviderProps> = ({children}) => {
    const [user, setUser] = useState<User | null>(null);

    const isAzureAuthenticated = useIsAuthenticated();

    const isUserRedirectedToSiteAfterLogin = useViewerStore(state => state.isUserRedirectedToSiteAfterLogin);

    useEffect(() => {
        const fetchData = async () => {
            await useOneButtonStore.getState().fetchOnebuttonCapabilities(true);
            const response = await userCapabilityService.validationTest();            

            const capabilities = new Set(response.capabilities.AppPage
                .filter(capability => capability.capability_value === "Yes")
                .map(capability => capability.capability_name));

            setUser({
                user_Id: response.user_Id,
                user_name: response.user_name,
                email: response.email,
                application_role_name: response.application_role_name,
                capabilities,
            });

            useViewerStore.setState({
                ghostOnlyGreenImpactTrees: resolveGhostOnlyGreenImpactTreesEnabled(
                    capabilities.has('GhostOnlyGreenImpactTrees'),
                ),
            });

            sessionStorage.setItem("userRole", response.application_role_name);
            sessionStorage.setItem(
                "showOneButtonBranding",
                capabilities.has("Show_OneButton") ? "1" : "0",
            );

        };


        if (isAzureAuthenticated ) {
            fetchData()
        }
    }, [isAzureAuthenticated, isUserRedirectedToSiteAfterLogin]);

    const hasCapability = (name: string) => {
            return user?.capabilities?.has(name) ?? false;
    };

    return (
        <UserContext.Provider value={{user, hasCapability}}>{children}</UserContext.Provider>
    );
};

export {UserProvider, useCurrentUser};
